/**
 * EntityStore — ECS-lite entity storage
 *
 * Entities are just IDs (integers). Components are plain objects
 * stored in typed Maps. Systems query by component presence.
 *
 * Object pooling: destroyed entities go to a free list and
 * their components are nulled out (not deleted) for reuse.
 *
 * This is intentionally simple — no archetype tables, no bitsets.
 * Good enough for 1000+ entities at 60fps in Canvas 2D.
 */

export class EntityStore {
  constructor() {
    this._nextId = 1;
    this._freeList = [];
    this._alive = new Set();

    // Component maps: componentName → Map(entityId → componentData)
    this._components = {};

    // Tag sets: tag → Set(entityId) — lightweight grouping
    this._tags = {};
  }

  // --- Entity lifecycle ---

  create() {
    let id;
    if (this._freeList.length > 0) {
      id = this._freeList.pop();
    } else {
      id = this._nextId++;
    }
    this._alive.add(id);
    return id;
  }

  destroy(id) {
    if (!this._alive.has(id)) return;
    this._alive.delete(id);

    // Clear all components for this entity
    for (const name in this._components) {
      this._components[name].delete(id);
    }

    // Clear all tags
    for (const tag in this._tags) {
      this._tags[tag].delete(id);
    }

    this._freeList.push(id);
  }

  isAlive(id) {
    return this._alive.has(id);
  }

  // --- Component operations ---

  addComponent(id, name, data) {
    if (!this._components[name]) {
      this._components[name] = new Map();
    }
    this._components[name].set(id, data);
    return this;
  }

  getComponent(id, name) {
    const map = this._components[name];
    return map ? map.get(id) : undefined;
  }

  hasComponent(id, name) {
    const map = this._components[name];
    return map ? map.has(id) : false;
  }

  removeComponent(id, name) {
    const map = this._components[name];
    if (map) map.delete(id);
  }

  // --- Tag operations ---

  addTag(id, tag) {
    if (!this._tags[tag]) {
      this._tags[tag] = new Set();
    }
    this._tags[tag].add(id);
    return this;
  }

  hasTag(id, tag) {
    return this._tags[tag] ? this._tags[tag].has(id) : false;
  }

  removeTag(id, tag) {
    if (this._tags[tag]) this._tags[tag].delete(id);
  }

  // --- Queries ---

  /** Get all alive entities with ALL specified components */
  query(...componentNames) {
    const results = [];

    // Start with the smallest component map for efficiency
    let smallest = null;
    let smallestSize = Infinity;
    for (const name of componentNames) {
      const map = this._components[name];
      if (!map) return results; // component type doesn't exist = no matches
      if (map.size < smallestSize) {
        smallest = map;
        smallestSize = map.size;
      }
    }

    if (!smallest) return results;

    for (const id of smallest.keys()) {
      if (!this._alive.has(id)) continue;
      let hasAll = true;
      for (const name of componentNames) {
        if (!this._components[name].has(id)) {
          hasAll = false;
          break;
        }
      }
      if (hasAll) results.push(id);
    }

    return results;
  }

  /** Get all alive entities with a specific tag */
  queryTag(tag) {
    const set = this._tags[tag];
    if (!set) return [];
    return [...set].filter(id => this._alive.has(id));
  }

  /** Get count of alive entities with a tag */
  countTag(tag) {
    const set = this._tags[tag];
    if (!set) return 0;
    let count = 0;
    for (const id of set) {
      if (this._alive.has(id)) count++;
    }
    return count;
  }

  /** Total alive entity count */
  get count() {
    return this._alive.size;
  }

  /** Clear everything (reset for new game) */
  clear() {
    this._alive.clear();
    this._freeList.length = 0;
    this._nextId = 1;
    for (const name in this._components) {
      this._components[name].clear();
    }
    for (const tag in this._tags) {
      this._tags[tag].clear();
    }
  }
}
